"use client"

import { useState } from "react"
import { Modal } from "@/components/ui/modal"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { InternalInput } from "@/components/ui/internal-input"
import { Handshake } from "lucide-react"

interface PartnerWithUsModalProps {
    isOpen: boolean
    onClose: () => void
}

export function PartnerWithUsModal({ isOpen, onClose }: PartnerWithUsModalProps) {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        contact: "",
        companyName: "",
        website: "",
        isServiceProvider: "no",
        isExpert: "no",
        message: ""
    })

    const [errors, setErrors] = useState<Record<string, string>>({})

    const handleChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev }
                delete newErrors[field]
                return newErrors
            })
        }
    }

    const validateForm = () => {
        const newErrors: Record<string, string> = {}

        if (!formData.name.trim()) newErrors.name = "Name is required"

        if (!formData.email.trim()) {
            newErrors.email = "Email is required"
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address"
        }

        if (!formData.contact.trim()) newErrors.contact = "Contact number is required"
        if (!formData.companyName.trim()) newErrors.companyName = "Company name is required"

        if (!formData.website.trim()) {
            newErrors.website = "Website is required"
        } else if (!/^https:\/\/[a-zA-Z0-9-.]+\.[a-zA-Z]{2,}(\/.*)?$/.test(formData.website)) {
            newErrors.website = "Website must start with https:// and be valid"
        }

        if (!formData.message.trim()) newErrors.message = "Please tell us how you would like to partner"

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        if (validateForm()) {
            // Handle form submission logic here
            console.log("Partner Inquiry:", formData)
            onClose()
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-xl">
            <div className="p-6">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4">
                    <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100">
                        <Handshake className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900">Partner with Deckwale</h2>
                        <p className="text-sm text-slate-500">Let's build something great together</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 pt-2">
                    <div className="grid grid-cols-2 gap-5">
                        <InternalInput
                            label="Name"
                            required
                            value={formData.name}
                            onChange={(e) => handleChange("name", e.target.value)}
                            placeholder="Your full name"
                            error={errors.name}
                        />
                        <InternalInput
                            label="Email ID"
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => handleChange("email", e.target.value)}
                            placeholder="work@company.com"
                            error={errors.email}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <InternalInput
                            label="Contact Number"
                            type="tel"
                            required
                            value={formData.contact}
                            onChange={(e) => handleChange("contact", e.target.value)}
                            placeholder="+91 00000 00000"
                            error={errors.contact}
                        />
                        <InternalInput
                            label="Company Name"
                            required
                            value={formData.companyName}
                            onChange={(e) => handleChange("companyName", e.target.value)}
                            placeholder="Your company name"
                            error={errors.companyName}
                        />
                    </div>

                    <InternalInput
                        label="Company Website"
                        required
                        value={formData.website}
                        onChange={(e) => handleChange("website", e.target.value)}
                        placeholder="https://example.com"
                        error={errors.website}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-2">
                        {/* Service Provider Question */}
                        <div className="space-y-3">
                            <Label className="text-xs font-semibold text-slate-500 block">
                                Are you a pitch deck building service provider? <span className="text-red-500">*</span>
                            </Label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <div className={`w-4 h-4 rounded-full border border-slate-300 flex items-center justify-center transition-colors ${formData.isServiceProvider === 'yes' ? 'border-blue-600' : 'group-hover:border-blue-400'}`}>
                                        {formData.isServiceProvider === 'yes' && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                                    </div>
                                    <input
                                        type="radio"
                                        name="serviceProvider"
                                        value="yes"
                                        checked={formData.isServiceProvider === "yes"}
                                        onChange={(e) => handleChange("isServiceProvider", e.target.value)}
                                        className="hidden"
                                    />
                                    <span className="text-sm text-slate-600">Yes</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <div className={`w-4 h-4 rounded-full border border-slate-300 flex items-center justify-center transition-colors ${formData.isServiceProvider === 'no' ? 'border-blue-600' : 'group-hover:border-blue-400'}`}>
                                        {formData.isServiceProvider === 'no' && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                                    </div>
                                    <input
                                        type="radio"
                                        name="serviceProvider"
                                        value="no"
                                        checked={formData.isServiceProvider === "no"}
                                        onChange={(e) => handleChange("isServiceProvider", e.target.value)}
                                        className="hidden"
                                    />
                                    <span className="text-sm text-slate-600">No</span>
                                </label>
                            </div>
                        </div>

                        {/* Expert Question */}
                        <div className="space-y-3">
                            <Label className="text-xs font-semibold text-slate-500 block">
                                Are you an expert in presentation making? <span className="text-red-500">*</span>
                            </Label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <div className={`w-4 h-4 rounded-full border border-slate-300 flex items-center justify-center transition-colors ${formData.isExpert === 'yes' ? 'border-blue-600' : 'group-hover:border-blue-400'}`}>
                                        {formData.isExpert === 'yes' && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                                    </div>
                                    <input
                                        type="radio"
                                        name="expert"
                                        value="yes"
                                        checked={formData.isExpert === "yes"}
                                        onChange={(e) => handleChange("isExpert", e.target.value)}
                                        className="hidden"
                                    />
                                    <span className="text-sm text-slate-600">Yes</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <div className={`w-4 h-4 rounded-full border border-slate-300 flex items-center justify-center transition-colors ${formData.isExpert === 'no' ? 'border-blue-600' : 'group-hover:border-blue-400'}`}>
                                        {formData.isExpert === 'no' && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                                    </div>
                                    <input
                                        type="radio"
                                        name="expert"
                                        value="no"
                                        checked={formData.isExpert === "no"}
                                        onChange={(e) => handleChange("isExpert", e.target.value)}
                                        className="hidden"
                                    />
                                    <span className="text-sm text-slate-600">No</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <InternalInput
                        label="How would you like to Partner with us?"
                        required
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={(e) => handleChange("message", e.target.value)}
                        placeholder="Tell us about your proposal..."
                        error={errors.message}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-50 mt-6">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onClose}
                            className="text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="bg-blue-600 hover:bg-blue-700 text-white min-w-[120px]"
                        >
                            Submit
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    )
}
